import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, race, timer } from 'rxjs';
import { map, mapTo, take } from 'rxjs/operators';
import { AppState } from '@root/app-state.module';
import { ReportsRoutes } from '@root/reports/components/reports-routes';
import { STATE_CODE_MAPPING } from '@root/reports/shared/constants/reports.constants';
import { RootRoutes } from '@root/root-routes';
import { ActionName } from '@shared/models/effects-names.model';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import {
  SET_UP_PROPERTIES_SUCCESS,
  SetUpProperties,
  SetUpPropertiesSuccess
} from '@store/reports/reports.actions';
import { PropertyIdentifier } from '@store/search-board/search-board.model';

/** All valid report route segments — auto-maintained from ReportsRoutes. */
const VALID_REPORT_CODES = new Set(Object.values(ReportsRoutes));

/** Navigate home if property resolution takes too long. */
const RESOLVE_TIMEOUT_MS = 29_000;

@Component({
  selector: 'rlst-callback-redirect',
  template: `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;">
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Handles the D2A OAuth callback redirect.
 * AuthGuard handles authentication and returnUrl restoration before this component runs.
 * On init, resolves the callback property and navigates to the requested report.
 */
export class CallbackRedirectComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store$: Store<AppState>,
    private readonly actions$: Actions,
    private readonly spinnerOverlayService: SpinnerOverlayService
  ) {
    this.destroyRef.onDestroy(() => this.spinnerOverlayService.hideSpinner(ActionName.SET_UP_PROPERTIES));
  }

  /** Reads `reportCode` from the route, waits for login and properties, then navigates. */
  ngOnInit(): void {
    const reportCode = this.route.snapshot.paramMap.get('reportCode');
    const fipsCode   = this.route.snapshot.paramMap.get('fipsCode');
    const apn        = this.route.snapshot.paramMap.get('apn');
    const normalizedFipsCode = fipsCode ? this.normalizeFipsCode(fipsCode) : null;

    if (!reportCode || !normalizedFipsCode || !apn || !VALID_REPORT_CODES.has(reportCode)) {
      this.router.navigate([RootRoutes.Search], { replaceUrl: true });
      return;
    }

    const propertyIdentifier = { fipsCode: normalizedFipsCode, apn } as PropertyIdentifier;

    this.spinnerOverlayService.showSpinner(ActionName.SET_UP_PROPERTIES);

    this.waitForPropertyResolution().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((property) => {
      this.spinnerOverlayService.hideSpinner(ActionName.SET_UP_PROPERTIES);
      if (!property) {
        this.router.navigate([RootRoutes.Search], { replaceUrl: true });
        return;
      }
      this.navigateToReport(reportCode);
    });

    this.store$.dispatch(new SetUpProperties({ propertyIdentifier, displayName: '' }));
  }

  private normalizeFipsCode(fipsCode: string): string | null {
    const normalizedFipsCode = fipsCode.trim().toUpperCase();

    if (/^\d{5}$/.test(normalizedFipsCode)) {
      return normalizedFipsCode;
    }

    const stateCountyMatch = /^([A-Z]{2})(\d{3})$/.exec(normalizedFipsCode);
    if (!stateCountyMatch) {
      return null;
    }

    const stateFipsCode = STATE_CODE_MAPPING[stateCountyMatch[1]];
    return stateFipsCode ? `${stateFipsCode}${stateCountyMatch[2]}` : null;
  }

  /** Races `SetUpPropertiesSuccess` vs a hard timeout; returns the payload or `null` on timeout. */
  private waitForPropertyResolution(): Observable<SetUpPropertiesSuccess['payload'] | null> {
    return race(
      this.actions$.pipe(
        ofType<SetUpPropertiesSuccess>(SET_UP_PROPERTIES_SUCCESS),
        map((action) => action.payload)
      ),
      timer(RESOLVE_TIMEOUT_MS).pipe(mapTo(null))
    ).pipe(take(1));
  }

  /**
   * Navigate to the report route, signalling `fromRedirect: true` so `ReportsGuard`
   * allows the navigation and lets the report component handle its own access overlay.
   */
  private navigateToReport(reportCode: string): void {
    const reportQueryParamMap: Record<string, string> = {
      [ReportsRoutes.FloodMap]:        'flood-map',
      [ReportsRoutes.PremiumFloodMap]: 'premium-flood-map',
      [ReportsRoutes.Hazard]:          'hazard',
      [ReportsRoutes.Foreclosure]:     'foreclosure',
      [ReportsRoutes.BuildingPermits]: 'building-permits',
      [ReportsRoutes.BuildingSketch]:  'building-sketch',
      [ReportsRoutes.AssessorMap]:     'assessor-map',
    };
    const type = reportQueryParamMap[reportCode] ?? reportCode;
    this.router.navigate(
      [RootRoutes.Reports, reportCode],
      { queryParams: { type }, state: { fromRedirect: true } }
    );
  }
}
